/**
 * @file Hook for campUSD operations using Nucleus SDK
 * @description Manages campUSD deposits and withdrawals using the Nucleus protocol
 */

import { useCallback, useState } from "react";
import {
  useAccount,
  useWriteContract,
  useWaitForTransactionReceipt,
} from "wagmi";
import {
  prepareCampUSDDeposit,
  prepareCampUSDWithdraw,
  prepareCampUSDDepositApproval,
  type CampUSDDepositParams,
  type CampUSDWithdrawParams,
} from "@/lib/nucleus/campusd";
import {
  type DepositAndBridgeTransactionData,
  type BridgeAndWithdrawTransactionData,
  type ApproveDepositTokenTransactionData,
} from "@molecularlabs/nucleus-frontend";

interface CampUSDOperationState {
  isLoading: boolean;
  isSuccess: boolean;
  isError: boolean;
  error: Error | null;
  txHash?: string;
  step?: "approving" | "depositing" | "withdrawing" | "completed";
}

export function useCampUSDNucleus() {
  const { address } = useAccount();
  const { writeContractAsync } = useWriteContract();

  const [operationState, setOperationState] = useState<CampUSDOperationState>({
    isLoading: false,
    isSuccess: false,
    isError: false,
    error: null,
  });

  /**
   * Deposits USDC and bridges to campUSD on Camp Network
   * Implements depositAndBridge flow: USDC (Ethereum) -> campUSD (Camp Network)
   */
  const depositCampUSD = useCallback(
    async (params: Omit<CampUSDDepositParams, "userAddress">) => {
      if (!address) {
        throw new Error("Wallet not connected");
      }

      setOperationState({
        isLoading: true,
        isSuccess: false,
        isError: false,
        error: null,
        step: "approving",
      });

      try {
        // Step 1: Prepare and execute USDC approval if needed
        const approvalData: ApproveDepositTokenTransactionData =
          await prepareCampUSDDepositApproval({
            ...params,
            userAddress: address,
          });

        setOperationState((prev) => ({ ...prev, step: "approving" }));

        // Execute approval transaction
        const approvalHash = await writeContractAsync({
          ...approvalData,
        });

        console.log("USDC approval transaction sent:", approvalHash);

        // Step 2: Prepare and execute deposit and bridge transaction
        setOperationState((prev) => ({ ...prev, step: "depositing" }));

        const depositData: DepositAndBridgeTransactionData =
          await prepareCampUSDDeposit({
            ...params,
            userAddress: address,
          });

        // Execute deposit and bridge transaction
        const depositHash = await writeContractAsync({
          ...depositData,
        });

        console.log(
          "campUSD deposit and bridge transaction sent:",
          depositHash,
        );

        setOperationState({
          isLoading: false,
          isSuccess: true,
          isError: false,
          error: null,
          txHash: depositHash,
          step: "completed",
        });

        return depositHash;
      } catch (error) {
        const errorMessage =
          error instanceof Error ? error : new Error("campUSD deposit failed");
        setOperationState({
          isLoading: false,
          isSuccess: false,
          isError: true,
          error: errorMessage,
        });
        throw errorMessage;
      }
    },
    [address, writeContractAsync],
  );

  /**
   * Withdraws campUSD and bridges back to USDC on Ethereum
   * Implements bridgeAndWithdraw flow: campUSD (Camp Network) -> USDC (Ethereum)
   */
  const withdrawCampUSD = useCallback(
    async (params: Omit<CampUSDWithdrawParams, "userAddress">) => {
      if (!address) {
        throw new Error("Wallet not connected");
      }

      setOperationState({
        isLoading: true,
        isSuccess: false,
        isError: false,
        error: null,
        step: "withdrawing",
      });

      try {
        // Prepare bridge and withdraw transaction
        const withdrawData: BridgeAndWithdrawTransactionData =
          await prepareCampUSDWithdraw({
            ...params,
            userAddress: address,
          });

        // Execute bridge transaction first
        const bridgeHash = await writeContractAsync({
          ...withdrawData.bridgeTransactionData,
        });

        console.log("campUSD bridge transaction sent:", bridgeHash);

        // Execute withdraw request transaction
        const withdrawHash = await writeContractAsync({
          ...withdrawData.withdrawTransactionData,
        });

        console.log("campUSD withdraw request sent:", withdrawHash);

        setOperationState({
          isLoading: false,
          isSuccess: true,
          isError: false,
          error: null,
          txHash: withdrawHash,
          step: "completed",
        });

        return withdrawHash;
      } catch (error) {
        const errorMessage =
          error instanceof Error ? error : new Error("campUSD withdraw failed");
        setOperationState({
          isLoading: false,
          isSuccess: false,
          isError: true,
          error: errorMessage,
        });
        throw errorMessage;
      }
    },
    [address, writeContractAsync],
  );

  /**
   * Checks if a specific amount can be deposited
   */
  const canDeposit = useCallback(
    (amount: string): boolean => {
      if (!address || !amount) return false;
      const depositAmount = parseFloat(amount);
      return depositAmount > 0;
    },
    [address],
  );

  /**
   * Checks if a specific amount can be withdrawn
   */
  const canWithdraw = useCallback(
    (amount: string): boolean => {
      if (!address || !amount) return false;
      const withdrawAmount = parseFloat(amount);
      return withdrawAmount > 0;
    },
    [address],
  );

  return {
    // State
    operationState,

    // Actions
    depositCampUSD,
    withdrawCampUSD,

    // Helpers
    canDeposit,
    canWithdraw,

    // Current state
    address,
  };
}

/**
 * Hook to wait for campUSD transaction confirmation
 */
export function useCampUSDTransaction(txHash?: string) {
  const {
    data: txReceipt,
    isLoading,
    isSuccess,
    isError,
  } = useWaitForTransactionReceipt({
    hash: txHash as `0x${string}`,
    query: {
      enabled: !!txHash,
    },
  });

  return {
    txReceipt,
    isConfirming: isLoading,
    isConfirmed: isSuccess,
    isError,
  };
}
